import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import logoReconcilia from '../logo-reconcilia.png';

export default function Firma() {
  const { token } = useParams<{ token: string }>();
  const [firmatario, setFirmatario] = useState<any>(null);
  const [stato, setStato] = useState<'caricamento' | 'pronto' | 'firmato' | 'errore'>('caricamento');
  const [messaggio, setMessaggio] = useState('');
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [fileFirmato, setFileFirmato] = useState<File | null>(null);
  const [caricando, setCaricando] = useState(false);

  useEffect(() => {
    caricaFirmatario();
  }, [token]);

  const caricaFirmatario = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL || 'https://firmereconcilia.it'}/api/firma/${token}`);
      setFirmatario(res.data);
      setStato('pronto');
    } catch (e: any) {
      setStato('errore');
      if (e.response?.status === 423) {
        setMessaggio('Un altro firmatario sta consultando il documento in questo momento. Riprovare tra qualche minuto.');
      } else {
        setMessaggio('Link non valido o scaduto.');
      }
    }
  };

  const scaricaPdf = async () => {
    try {
      const res = await fetch(firmatario.pdf_url);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'verbale_da_firmare.pdf';
      a.click();
      window.URL.revokeObjectURL(url);
      setStep(2);
    } catch (e) {
      setMessaggio('❌ Errore nel download del PDF. Riprovare.');
    }
  };

  const caricaPdfFirmato = async () => {
    if (!fileFirmato) return;
    setCaricando(true);
    try {
      const formData = new FormData();
      formData.append('file', fileFirmato);
      await axios.post(`${process.env.REACT_APP_API_URL || 'https://firmereconcilia.it'}/api/firma/${token}/upload-firmato`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setStato('firmato');
      setMessaggio('✅ Documento firmato caricato con successo! Grazie.');
    } catch (e) {
      setMessaggio('❌ Errore durante il caricamento. Riprovare.');
    } finally {
      setCaricando(false);
    }
  };

  const header = (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', marginBottom: '1.5rem', paddingBottom: '1rem', borderBottom: '3px solid #9f0408' }}>
      <img src={logoReconcilia} alt="Reconcilia ADR" style={{ height: '52px', width: 'auto' }} />
      <div>
        <div style={{ color: '#9f0408', fontWeight: 'bold', fontSize: '1.2rem' }}>Reconcilia ADR</div>
        <div style={{ color: '#666', fontSize: '0.8rem' }}>Alternative Dispute Resolution</div>
      </div>
    </div>
  );

  if (stato === 'caricamento') return (
    <div style={containerStyle}>
      {header}
      <p style={{ color: '#666' }}>Caricamento in corso...</p>
    </div>
  );

  if (stato === 'errore') return (
    <div style={containerStyle}>
      {header}
      <div style={{ background: '#fdecea', border: '1px solid #f5c6cb', borderRadius: '8px', padding: '1rem', color: '#9f0408' }}>
        {messaggio}
      </div>
    </div>
  );

  if (stato === 'firmato') return (
    <div style={containerStyle}>
      {header}
      <div style={{ background: '#eafaf1', border: '1px solid #a9dfbf', borderRadius: '8px', padding: '1.5rem', textAlign: 'center' }}>
        <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>✅</div>
        <h2 style={{ color: '#1e8449', margin: '0 0 0.5rem' }}>Firma completata</h2>
        <p style={{ color: '#555', margin: 0 }}>{messaggio}</p>
        <p style={{ color: '#888', marginTop: '0.5rem', fontSize: '0.9rem' }}>Può chiudere questa pagina.</p>
      </div>
    </div>
  );

  return (
    <div style={containerStyle}>
      {header}
      <h2 style={{ color: '#333', marginTop: 0 }}>Richiesta di firma digitale</h2>

      {firmatario && (
        <div style={cardStyle}>
          <p><b>Gentile {firmatario.nome},</b></p>
          <p>È richiesta la sua firma digitale per il verbale del:</p>
          <p style={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#2c3e50' }}>
            {firmatario.verbale_data}
          </p>

          {/* STEP 1 — Scarica il PDF */}
          <div style={stepStyle(step === 1)}>
            <h3>📥 Passo 1 — Scarichi il documento</h3>
            <p>Scarichi il documento PDF che deve firmare.</p>
            {firmatario.pdf_url ? (
              <button onClick={scaricaPdf} style={btnLink}>
                📄 Scarica il documento PDF
              </button>
            ) : (
              <p style={{ color: 'red' }}>Documento non ancora disponibile.</p>
            )}
          </div>

          {/* STEP 2 — Firma con il software */}
          <div style={stepStyle(step === 2)}>
            <h3>✍️ Passo 2 — Firmi il documento</h3>
            <p>Apra il PDF scaricato con il suo software di firma digitale e apponga la sua firma.</p>
            <div style={{ background: '#eaf4fb', padding: '1rem', borderRadius: '8px', marginBottom: '1rem' }}>
              <p><b>Software compatibili:</b></p>
              <p>• ArubaSign (Aruba)</p>
              <p>• Dike 6 (InfoCert)</p>
              <p>• FirmaOK! (Poste Italiane)</p>
              <p>• Qualsiasi software di firma CAdES o PAdES</p>
            </div>
            <button onClick={() => setStep(3)} style={btnSecondario}>
              ✅ Ho firmato il documento, procedo al caricamento →
            </button>
          </div>

          {/* STEP 3 — Carica il PDF firmato */}
          <div style={stepStyle(step === 3)}>
            <h3>📤 Passo 3 — Carichi il documento firmato</h3>
            <p>Carichi il file PDF firmato (formato .pdf).</p>
            <input
              type="file"
              accept=".pdf"
              onChange={e => e.target.files && setFileFirmato(e.target.files[0])}
              style={{ marginBottom: '1rem', display: 'block' }}
            />
            {fileFirmato && (
              <p style={{ color: '#27ae60' }}>✅ File selezionato: {fileFirmato.name}</p>
            )}
            {messaggio && (
              <p style={{ color: 'red' }}>{messaggio}</p>
            )}
            <button
              onClick={caricaPdfFirmato}
              disabled={!fileFirmato || caricando}
              style={{ ...firmaBtn, opacity: !fileFirmato || caricando ? 0.6 : 1 }}
            >
              {caricando ? 'Caricamento in corso...' : '📤 Invia documento firmato'}
            </button>
          </div>

        </div>
      )}
    </div>
  );
}

const containerStyle: React.CSSProperties = {
  padding: '2rem',
  fontFamily: 'Arial, sans-serif',
  maxWidth: '700px',
  margin: '0 auto'
};

const cardStyle: React.CSSProperties = {
  border: '1px solid #ddd',
  borderRadius: '8px',
  padding: '2rem',
  background: '#fafafa',
  marginTop: '1rem'
};

const stepStyle = (attivo: boolean): React.CSSProperties => ({
  border: `2px solid ${attivo ? '#9f0408' : '#e0e0e0'}`,
  borderRadius: '8px',
  padding: '1.5rem',
  marginBottom: '1rem',
  background: attivo ? '#fff8f8' : '#f9f9f9',
  opacity: attivo ? 1 : 0.5
});

const btnLink: React.CSSProperties = {
  display: 'inline-block',
  padding: '0.8rem 1.5rem',
  background: '#9f0408',
  color: 'white',
  borderRadius: '6px',
  textDecoration: 'none',
  fontWeight: 'bold',
  border: 'none',
  cursor: 'pointer',
  fontSize: '1rem'
};

const btnSecondario: React.CSSProperties = {
  padding: '0.8rem 1.5rem',
  background: '#27ae60',
  color: 'white',
  border: 'none',
  borderRadius: '6px',
  fontSize: '1rem',
  cursor: 'pointer'
};

const firmaBtn: React.CSSProperties = {
  padding: '1rem 2rem',
  background: '#9f0408',
  color: 'white',
  border: 'none',
  borderRadius: '8px',
  fontSize: '1.1rem',
  cursor: 'pointer',
  width: '100%'
};